import { Check } from "lucide-react";
import type { AccountId } from "@budgeter/shared";
import { useSnapshot } from "../../data/StoreContext";
import { accountsWithBalances, type AccountWithBalance } from "../../data/selectors";
import { cn, Money, Sheet } from "../../ui";

export interface AccountPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: AccountId | null;
  onPick: (accountId: AccountId) => void;
  /** Only offer on-budget accounts (Quick Add). */
  onBudgetOnly?: boolean;
}

const headingClass = "px-1 pt-3 pb-1 text-[12px] font-bold tracking-[.5px] text-muted uppercase";

/** Account chooser sheet: open on-budget accounts, then off-budget ones, with balances. */
export function AccountPicker({
  open,
  onOpenChange,
  value,
  onPick,
  onBudgetOnly,
}: AccountPickerProps) {
  const s = useSnapshot();
  const { onBudget, offBudget } = accountsWithBalances(s);
  const row = (a: AccountWithBalance) => (
    <button
      key={a.id}
      type="button"
      className="flex min-h-[50px] w-full items-center gap-3 border-b border-line-soft px-1 text-left text-[15.5px] text-ink"
      aria-current={a.id === value ? "true" : undefined}
      onClick={() => {
        onPick(a.id);
        onOpenChange(false);
      }}
    >
      <span className={cn("min-w-0 flex-1 truncate", a.id === value && "font-extrabold")}>
        {a.name}
      </span>
      {a.id === value && <Check size={16} className="text-accent" aria-hidden />}
      <Money cents={a.balance.working} tone="negRed" className="text-[14px] text-muted" />
    </button>
  );
  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="Account">
      <h3 className={headingClass}>On budget</h3>
      {onBudget.map(row)}
      {!onBudgetOnly && offBudget.length > 0 && (
        <>
          <h3 className={headingClass}>Off budget</h3>
          {offBudget.map(row)}
        </>
      )}
    </Sheet>
  );
}
